import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TbSearch, TbUserCheck } from 'react-icons/tb';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { PageState } from '../components/PageState';
import { coachApi as defaultCoachApi } from './coach-api';
import './coach.css';

export function CoachHomePage({ api = defaultCoachApi }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', members: [], message: '' });
  const [query, setQuery] = useState('');

  const load = useCallback(() => {
    let active = true;
    setState({ status: 'loading', members: [], message: '' });
    api.listMembers()
      .then((members) => { if (active) setState({ status: 'ready', members: members || [], message: '' }); })
      .catch((error) => {
        if (!active) return;
        const outcome = handleApiError(error, { clearBusinessData: () => setState({ status: 'loading', members: [], message: '' }) });
        if (!outcome.handled) setState({ status: 'error', members: [], message: errorMessageFor(error) });
      });
    return () => { active = false; };
  }, [api, handleApiError]);

  useEffect(() => load(), [load]);

  const filteredMembers = useMemo(() => {
    const search = query.trim().toLocaleLowerCase();
    return search
      ? state.members.filter(member => (member.username || member.email || '').toLocaleLowerCase().includes(search))
      : state.members;
  }, [query, state.members]);

  if (state.status === 'loading') return <PageState title="Mis socios" status="loading" />;
  if (state.status === 'error') {
    return <PageState title="Mis socios" status="error" message={state.message} action={<button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button>} />;
  }

  return (
    <section className="gym-page gym-coach-home" aria-labelledby="gym-page-title">
      <header className="gym-page__header gym-coach-home__header">
        <div>
          <h1 id="gym-page-title" className="gym-page__title">Mis socios</h1>
          <p className="gym-coach-home__lead">Abrí una ficha para revisar el entrenamiento y la asistencia.</p>
        </div>
        <p className="gym-coach-home__count" aria-label={`${state.members.length} socios`}>
          <strong>{state.members.length}</strong><span>socios</span>
        </p>
      </header>
      {state.members.length === 0 ? (
        <div className="gym-coach-empty">
          <h2>Todavía no hay socios cargados</h2>
        </div>
      ) : (
        <>
          <div className="gym-coach-home__search">
            <label className="gym-sr-only" htmlFor="coach-member-search">Buscar socio</label>
            <TbSearch aria-hidden="true" focusable="false" />
            <input id="coach-member-search" type="search" placeholder="Buscar socio" value={query} onChange={event => setQuery(event.target.value)} />
          </div>
          {filteredMembers.length === 0 ? <p className="gym-muted-text">No hay socios que coincidan</p> : (
            <ul className="gym-coach-members">
              {filteredMembers.map((member) => {
                const name = member.username || member.email || 'Socio';
                return (
                  <li key={member.id}>
                    <article className="gym-coach-member-row">
                      <div className="gym-coach-member-row__identity">
                        <span className="gym-coach-member-row__avatar" aria-hidden="true">{name.trim().charAt(0).toUpperCase() || '?'}</span>
                        <div className="gym-coach-member-row__text">
                          <div className="gym-coach-member-row__title">
                            <h2>{name}</h2>
                            {member.assignedToMe && <span className="gym-badge gym-coach-member-row__badge"><TbUserCheck aria-hidden="true" focusable="false" />A tu cargo</span>}
                          </div>
                          <p>{member.email || 'Sin correo registrado'}</p>
                          {member.profile?.fitnessGoal && <span className="gym-coach-member-row__goal">Objetivo: {member.profile.fitnessGoal}</span>}
                        </div>
                      </div>
                      <Link className="gym-button gym-button--secondary gym-coach-member-row__open" aria-label={`Ver ficha de ${name}`} to={`/coach/socios/${encodeURIComponent(member.id)}`}>Ver ficha</Link>
                    </article>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
